import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Badge, Button, Input, Select } from '@components/ui';
import { PRIORITY_CONFIG, TYPE_CONFIG } from '@config/constants';
import { useUpdateDraft, useApproveDrafts, useDiscardDraft, useRestoreDraft } from '@hooks';

const TYPE_OPTIONS = ['TASK', 'BUG', 'FEATURE', 'IMPROVEMENT'].map((value) => ({
  value,
  label: `${TYPE_CONFIG[value.toLowerCase()].icon} ${TYPE_CONFIG[value.toLowerCase()].label}`,
}));

const PRIORITY_OPTIONS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((value) => ({
  value,
  label: `${PRIORITY_CONFIG[value.toLowerCase()].icon} ${PRIORITY_CONFIG[value.toLowerCase()].label}`,
}));

const CONFIDENCE_LABEL = { HIGH: 'alta', MEDIUM: 'media', LOW: 'baja' };
const CONFIDENCE_VARIANT = { HIGH: 'success', MEDIUM: 'warning', LOW: 'danger' };

const toForm = (draft) => ({
  title: draft.title || '',
  description: draft.description || '',
  type: draft.type,
  priority: draft.priority,
  assigneeId: draft.assigneeId ? String(draft.assigneeId) : '',
  dueDate: draft.dueDate ? draft.dueDate.slice(0, 10) : '',
});

const toPayload = (form) => ({
  title: form.title.trim(),
  description: form.description.trim() || null,
  type: form.type,
  priority: form.priority,
  assigneeId: form.assigneeId ? Number(form.assigneeId) : null,
  dueDate: form.dueDate || null,
});

export default function MeetingDraftCard({ draft, meetingId, members, canAssign, selected, onToggleSelect, onOpenTask }) {
  const [form, setForm] = useState(() => toForm(draft));
  const [error, setError] = useState('');

  const updateDraft = useUpdateDraft();
  const approveDrafts = useApproveDrafts();
  const discardDraft = useDiscardDraft();
  const restoreDraft = useRestoreDraft();

  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(toForm(draft)), [form, draft]);
  const busy = updateDraft.isPending || approveDrafts.isPending || discardDraft.isPending || restoreDraft.isPending;
  const memberOptions = members.map((m) => ({ value: String(m.id), label: m.name }));

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const run = async (action) => {
    setError('');
    try {
      await action();
    } catch (err) {
      setError(err.message);
    }
  };

  const save = () => updateDraft.mutateAsync({ draftId: draft.id, data: toPayload(form) });

  const handleApprove = () =>
    run(async () => {
      if (!form.title.trim()) throw new Error('El título es requerido');
      if (isDirty) await save();
      await approveDrafts.mutateAsync({ meetingId, draftIds: [draft.id] });
    });

  if (draft.status === 'APPROVED') {
    return (
      <Card padding="sm" className="border-l-4 border-l-emerald-500">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{draft.title}</p>
            <p className="text-xs text-slate-500">
              {draft.task ? `Tarea #${draft.task.id} creada` : 'Tarea creada y luego eliminada'}
            </p>
          </div>
          {draft.task && (
            <Button variant="ghost" size="sm" onClick={() => onOpenTask(draft.task.id)}>
              Ver tarea
            </Button>
          )}
        </div>
      </Card>
    );
  }

  if (draft.status === 'DISCARDED') {
    return (
      <Card padding="sm" className="opacity-60">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm line-through truncate">{draft.title}</p>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => run(() => restoreDraft.mutateAsync(draft.id))}>
            Restaurar
          </Button>
        </div>
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      </Card>
    );
  }

  const blockedByAssign = !canAssign && !!form.assigneeId;

  return (
    <Card padding="md">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1.5 h-4 w-4 accent-indigo-600"
          checked={selected}
          onChange={() => onToggleSelect(draft.id)}
          aria-label={`Seleccionar ${draft.title}`}
        />
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="slate">Borrador</Badge>
            {draft.confidence && (
              <Badge variant={CONFIDENCE_VARIANT[draft.confidence]}>Confianza {CONFIDENCE_LABEL[draft.confidence]}</Badge>
            )}
            {isDirty && <span className="text-xs text-amber-600">Cambios sin guardar</span>}
          </div>

          <Input name={`title-${draft.id}`} value={form.title} onChange={handleChange('title')} placeholder="Título" />

          <textarea
            className="input w-full text-sm"
            rows={3}
            value={form.description}
            onChange={handleChange('description')}
            placeholder="Descripción"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <Select label="Tipo" name={`type-${draft.id}`} value={form.type} onChange={handleChange('type')} options={TYPE_OPTIONS} placeholder="Tipo" />
            <Select label="Prioridad" name={`priority-${draft.id}`} value={form.priority} onChange={handleChange('priority')} options={PRIORITY_OPTIONS} placeholder="Prioridad" />
            <Select
              label="Responsable"
              name={`assignee-${draft.id}`}
              value={form.assigneeId}
              onChange={handleChange('assigneeId')}
              options={memberOptions}
              placeholder="Sin asignar"
            />
            <Input label="Fecha compromiso" type="date" name={`due-${draft.id}`} value={form.dueDate} onChange={handleChange('dueDate')} />
          </div>

          {(draft.dueDateText || draft.assigneeName) && (
            <p className="text-xs text-slate-500">
              {draft.dueDateText && <>Fecha dicha en la reunión: «{draft.dueDateText}». </>}
              {draft.assigneeName && <>Responsable mencionado (no es miembro): {draft.assigneeName}.</>}
            </p>
          )}

          {draft.evidence && (
            <blockquote className="border-l-2 border-slate-300 pl-3 text-xs italic text-slate-500">“{draft.evidence}”</blockquote>
          )}

          {blockedByAssign && (
            <p className="text-xs text-amber-700">
              No tienes permiso para asignar tareas. Quita el responsable o pide a un manager que lo apruebe.
            </p>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => run(() => discardDraft.mutateAsync(draft.id))}>
              Descartar
            </Button>
            {isDirty && (
              <Button variant="outline" size="sm" disabled={busy || !form.title.trim()} onClick={() => run(save)}>
                Guardar
              </Button>
            )}
            <Button variant="success" size="sm" loading={approveDrafts.isPending} disabled={busy || blockedByAssign} onClick={handleApprove}>
              Crear tarea
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

MeetingDraftCard.propTypes = {
  draft: PropTypes.object.isRequired,
  meetingId: PropTypes.number.isRequired,
  members: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
  canAssign: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onOpenTask: PropTypes.func.isRequired,
};
